import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Chip,
  Collapse
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../../auth/useAuth';
import mockUsers from '../../mocks/users.json';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMockUsers, setShowMockUsers] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/patient/home');
    } catch (err) {
      setError(authError || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleMockUserLogin = async (userEmail: string, userPassword: string, role: string) => {
    setEmail(userEmail);
    setPassword(userPassword);
    setError('');
    setLoading(true);

    try {
      await login(userEmail, userPassword);
      if (role === 'PATIENT') {
        navigate('/patient/home');
      } else if (role === 'NUTRITIONIST') {
        navigate('/nutritionist/dashboard');
      } else if (role === 'ADMIN') {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(authError || 'Failed to login with mock user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6 }}>
        <Typography variant="h4" gutterBottom align="center">
          Sign In
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Welcome back to CGM Nutrition System
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Mock Users Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowMockUsers(!showMockUsers)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">Quick Login (Demo Accounts)</Typography>
              <Chip label="Development Mode" size="small" color="warning" />
            </Box>
            {showMockUsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>

          <Collapse in={showMockUsers}>
            <Box sx={{ mt: 3 }}>
              {/* Patients */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" />
                Patient Accounts
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {mockUsers.patients.map((patient) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={patient.id}>
                    <Card variant="outlined">
                      <CardActionArea
                        onClick={() => handleMockUserLogin(patient.email, patient.password, patient.role)}
                        disabled={loading}
                      >
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <PersonIcon color="primary" sx={{ mb: 1 }} />
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {patient.firstName} {patient.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {patient.email}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Nutritionists */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalHospitalIcon fontSize="small" />
                Nutritionist Accounts
              </Typography>
              <Grid container spacing={2}>
                {mockUsers.nutritionists.map((nutritionist) => (
                  <Grid item xs={12} sm={6} md={4} key={nutritionist.id}>
                    <Card variant="outlined">
                      <CardActionArea
                        onClick={() => handleMockUserLogin(nutritionist.email, nutritionist.password, nutritionist.role)}
                        disabled={loading}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocalHospitalIcon color="secondary" />
                            <Typography variant="body2" fontWeight={600}>
                              {nutritionist.firstName} {nutritionist.lastName}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {nutritionist.credentials}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {nutritionist.organization}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Admins */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AdminPanelSettingsIcon fontSize="small" />
                Organization Admin Accounts
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {mockUsers.admins.map((admin) => (
                  <Grid item xs={12} sm={6} md={4} key={admin.id}>
                    <Card variant="outlined">
                      <CardActionArea
                        onClick={() => handleMockUserLogin(admin.email, admin.password, admin.role)}
                        disabled={loading}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AdminPanelSettingsIcon color="error" />
                            <Typography variant="body2" fontWeight={600}>
                              {admin.firstName} {admin.lastName}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {admin.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {admin.organization}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                All demo accounts use password: <strong>patient123</strong> or <strong>nutritionist123</strong> or <strong>admin123</strong>
              </Alert>
            </Box>
          </Collapse>
        </Paper>

        {/* Traditional Login Form */}
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom align="center">
            Or Sign In with Credentials
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !email || !password}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Don&apos;t have an account?{' '}
                <Link component={RouterLink} to="/register/patient">
                  Register as Patient
                </Link>
                {' or '}
                <Link component={RouterLink} to="/register/nutritionist">
                  Register as Nutritionist
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
